import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Bell, CalendarDays, Timer as TimerIcon, Trophy } from "lucide-react";
import i18n from "@/i18n/config";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useWidgetConfig } from "@/hooks/use-widget-config";
import { useRoleName } from "@/hooks/use-account-type";
import { enabledModules, isModuleAvailable, type WidgetModuleId, WIDGET_MODULES } from "@/lib/widget-config";
import {
  ReminderWidget,
  TimerWidget,
  AgendaWidget,
  ProgressWidget,
} from "@/components/widgets/StudyWidgets";

export const Route = createFileRoute("/widget")({
  ssr: false,
  head: () => {
    const t = i18n.t.bind(i18n);
    const title = t("widgets.page.metaTitle");
    return {
      meta: [
        { title },
        { name: "description", content: t("widgets.page.metaDescription") },
        { property: "og:title", content: title },
        { property: "og:description", content: t("widgets.page.metaOgDescription") },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
      ],
    };
  },
  component: WidgetPage,
});

const MODULE_ICONS: Record<WidgetModuleId, typeof Bell> = {
  reminder: Bell,
  timer: TimerIcon,
  progress: Trophy,
  agenda: CalendarDays,
};

function WidgetPage() {
  const { t } = useTranslation();
  const { config, roles, toggleModule, setSourceRole } = useWidgetConfig();
  const roleName = useRoleName();
  const active = enabledModules(config);
  const [preview, setPreview] = useState<WidgetModuleId>(active[0] ?? "reminder");

  useEffect(() => {
    if (!active.includes(preview) && active[0]) setPreview(active[0]);
  }, [active.join(","), preview]);

  return (
    <div className="min-h-dvh bg-background px-5 py-8 safe-x">
      <div className="mx-auto w-full max-w-md space-y-5">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">{t("widgets.page.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("widgets.settings.subtitle")}</p>
        </header>

        {/* Moduli */}
        <section className="rounded-3xl border border-border bg-card p-4">
          <h2 className="text-sm font-semibold">{t("widgets.settings.modulesTitle")}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("widgets.settings.modulesHint")}</p>
          <div className="mt-3 space-y-1">
            {WIDGET_MODULES.map((id) => {
              const Icon = MODULE_ICONS[id];
              const available = isModuleAvailable(id, config.sourceRole);
              const on = config.modules[id];
              const lastOne = on && active.length === 1;
              return (
                <div
                  key={id}
                  className={cn(
                    "flex items-center gap-3 rounded-2xl px-2 py-2.5",
                    !available && "opacity-50",
                  )}
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-muted">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <Label htmlFor={`widget-${id}`} className="min-w-0 flex-1 cursor-pointer">
                    <span className="block text-sm font-medium">{t(`widgets.kinds.${id}.title`)}</span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      {available ? t(`widgets.kinds.${id}.sub`) : t("widgets.settings.studentOnly")}
                    </span>
                  </Label>
                  <Switch
                    id={`widget-${id}`}
                    checked={on}
                    disabled={!available || lastOne}
                    onCheckedChange={(v) => toggleModule(id, v)}
                    aria-label={t(`widgets.kinds.${id}.title`)}
                  />
                </div>
              );
            })}
          </div>
          {active.length === 1 && (
            <p className="mt-2 text-xs text-muted-foreground">{t("widgets.settings.keepOne")}</p>
          )}
        </section>

        {/* Profilo sorgente */}
        <section className="rounded-3xl border border-border bg-card p-4">
          <h2 className="text-sm font-semibold">{t("widgets.settings.profileTitle")}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("widgets.settings.profileHint")}</p>
          <div className="mt-3 grid gap-2" role="radiogroup" aria-label={t("widgets.settings.profileTitle")}>
            {roles.map((role) => {
              const selected = config.sourceRole === role;
              return (
                <button
                  key={role}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setSourceRole(role)}
                  className={cn(
                    "min-h-11 rounded-2xl border px-3 py-2.5 text-left text-sm transition-colors",
                    selected
                      ? "border-primary bg-primary/10 font-medium"
                      : "border-border text-muted-foreground hover:bg-muted",
                  )}
                >
                  {roleName(role)}
                </button>
              );
            })}
          </div>
          {roles.length === 1 && (
            <p className="mt-2 text-xs text-muted-foreground">{t("widgets.settings.singleProfile")}</p>
          )}
        </section>

        {/* Anteprima */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold">{t("widgets.settings.previewTitle")}</h2>
          {active.length > 1 && (
            <div className="flex gap-1.5 rounded-2xl bg-muted p-1">
              {active.map((id) => (
                <button
                  key={id}
                  onClick={() => setPreview(id)}
                  className={cn(
                    "flex-1 rounded-xl px-3 py-2 text-xs font-medium transition-colors",
                    preview === id ? "bg-background shadow-sm" : "text-muted-foreground",
                  )}
                >
                  {t(`widgets.kinds.${id}.title`)}
                </button>
              ))}
            </div>
          )}
          {preview === "reminder" && <ReminderWidget />}
          {preview === "timer" && <TimerWidget />}
          {preview === "progress" && <ProgressWidget />}
          {preview === "agenda" && <AgendaWidget />}
        </section>

        <div className="rounded-3xl border border-dashed border-border p-4 text-xs leading-relaxed text-muted-foreground">
          <p className="font-semibold text-foreground">{t("widgets.page.howTo")}</p>
          <p className="mt-1">
            {t("widgets.page.iphoneHint")}
            <br />
            {t("widgets.page.androidHint")}
          </p>
        </div>

        <Link to="/">
          <Button variant="outline" className="w-full rounded-2xl">
            {t("widgets.page.backToApp")}
          </Button>
        </Link>
      </div>
    </div>
  );
}
